export type FieldType =
  | "text"
  | "textarea"
  | "date"
  | "email"
  | "radio"
  | "radio_vertical"
  | "checkbox"
  | "checkbox_vertical"
  | "likert"
  | "file"
  | "text_no_input"
  | "file_no_input"
  | "person"
  | "currency"
  | "address";

export interface FieldConfig {
  id: string;
  question: string;
  shortLabel?: string;
  type: FieldType;
  required?: boolean;
  hint?: string;
  options?: string[];
  likertRows?: string[];
  likertColumns?: string[];
  /** ID of another field whose value provides context (e.g. a name field for address lookup) */
  contextField?: string;
  /** Maximum number of files allowed (file type only) */
  maxFiles?: number;
  /** Maximum file size in MB per upload (file type only) */
  maxFileSizeMB?: number;
  /** Maximum number of people selectable (person type only; undefined = unlimited) */
  maxPersons?: number;
  /** Text presented to respondents without collecting an answer (text_no_input only). */
  displayText?: string;
  /** File presented to respondents without collecting an answer (file_no_input only). */
  attachment?: { name: string; url: string };
}

export interface SectionConfig {
  title: string;
  shortTitle: string;
  fields: FieldConfig[];
  /** When true, the section is hidden from the form and its fields are not required. */
  hidden?: boolean;
}

const YES_NO = ["Yes", "No"];

export const defaultFormConfig: SectionConfig[] = [
  {
    title: "Project Overview",
    shortTitle: "Overview",
    fields: [
      { id: "projectName", question: "Project Name", type: "text", required: true },
      { id: "jobNumber", question: "Job Number", type: "text", required: true },
      { id: "projectDescription", question: "Project Description", type: "textarea", required: true },
      {
        id: "projectType",
        question: "Project Type",
        type: "radio_vertical",
        required: true,
        options: ["Office", "Industrial / Warehouse", "Multi-Family", "Healthcare", "Retail", "Mixed-Use", "Other"]
      },
      {
        id: "division",
        question: "Which division is pursuing this project?",
        type: "radio",
        required: true,
        options: ["Denver", "Kansas City", "Phoenix", "Richmond", "St. Louis"],
        hint: "Business Unit"
      },
      { id: "isPreconOnly", question: "Is this a preconstruction-only engagement?", type: "radio", required: true, options: YES_NO }
    ]
  },
  {
    title: "Preconstruction Team",
    shortTitle: "Team",
    fields: [
      { id: "preconLead", question: "Preconstruction Lead", type: "person", required: true },
      { id: "estimator", question: "Estimator", type: "person", required: true },
      { id: "projectExecutive", question: "Project Executive", type: "person", required: true },
      { id: "clientName", question: "Client / Owner Name", type: "text", required: true },
      { id: "clientContactEmail", question: "Client Contact Email", type: "email", required: true }
    ]
  },
  {
    title: "Schedule & Budget",
    shortTitle: "Schedule",
    fields: [
      { id: "bidDueDate", question: "Bid Due Date", type: "date", required: true },
      { id: "constructionStart", question: "Anticipated Construction Start", type: "date", required: true },
      { id: "estimatedContractValue", question: "Estimated Contract Value", type: "currency", required: true },
      { id: "preconFeeAmount", question: "Preconstruction Fee Amount", type: "currency", required: false },
      {
        id: "deliveryMethod",
        question: "Anticipated Delivery Method",
        type: "radio",
        required: true,
        options: ["CM at Risk", "Design-Build", "General Contracting", "CM Fee Only"]
      },
      { id: "additionalNotes", question: "Additional Notes", type: "textarea", required: false }
    ]
  }
];

export let formConfig: SectionConfig[] = JSON.parse(JSON.stringify(defaultFormConfig));
export let allFields: FieldConfig[] = formConfig.flatMap((s) => s.fields);

const listeners: Array<() => void> = [];
export function onFormConfigChange(fn: () => void) { listeners.push(fn); }
export function setFormConfigData(c: SectionConfig[]) {
  formConfig = c;
  allFields = c.flatMap((s) => s.fields);
  for (const fn of listeners) fn();
}
